import { Option } from "components/SingleSelect/types";
import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "slice";
import { useGetCurrenciesQuery } from "slice/common";
import {
    updateNewJobData,
    useCreateJobMutation,
    useUpdateJobMutation,
} from "slice/jobs";
import { ApiError, CreateUpdateJobParams } from "slice/jobs/types";
import { getErrors } from "utils/getErrors";
import { validations } from "utils/validations";
import { useEditJob } from "../useEditJob";
import { BudgetDetails } from "./types";

export const useJobBudget = () => {
    const { jobs, user } = useAppSelector((state) => state);

    const isEdit = jobs.newJobData?.isEdit;

    const navigateTo = useNavigate();

    const [budgetDetails, setBudgetDetails] = useState<BudgetDetails>({
        payType: "professional_pay",
        durationCycle: "",
        currency: "AED",
        minAmount: "",
        maxAmount: "",
        additionalCompensation: "",
        status: "",
    });

    const [budgetDetailsErrors, setBudgetDetailsErrors] =
        useState<BudgetDetails>({
            payType: "",
            durationCycle: "",
            currency: "",
            minAmount: "",
            maxAmount: "",
            additionalCompensation: "",
            status: "",
        });

    useEditJob();

    const [currenciesData, setCurrenciesData] = useState<Option[]>([]);

    const { id: job_id } = useParams();

    const { data: currencies, isFetching } = useGetCurrenciesQuery({});

    const [
        createJob,
        {
            data: createData,
            isLoading: isCreateLoading,
            isSuccess: isCreateSuccess,
            isError: isCreateError,
            error: createError,
        },
    ] = useCreateJobMutation();

    const [
        updateJob,
        {
            data: updateData,
            isLoading: isUpdateLoading,
            isSuccess: isUpdateSuccess,
        },
    ] = useUpdateJobMutation();

    const dispatch = useDispatch();

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const handleJobDetailsStartDateChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setStartDate(value);
        // dispatch(updateNewJobData({ ...newJobData, startDate: value }));
    };

    const handleJobDetailsEndDateChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setEndDate(value);
    };

    const handleTextInputChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        const input = Number(value);
        if (["minAmount", "maxAmount"].includes(name) && input > 1000000) {
            return;
        }
        setBudgetDetails((prev) => ({
            ...prev,
            [name]: value,
        }));

        setBudgetDetailsErrors((prev) => ({
            ...prev,
            [name]: validations(name, value),
        }));
    };

    const handleDropdownChange = (name: string, value: string) => {
        setBudgetDetails((prev) => ({
            ...prev,
            [name]: value,
        }));

        setBudgetDetailsErrors((prev) => ({
            ...prev,
            [name]: validations(name, value),
        }));
    };

    const handleBack = () => {
        if (isEdit) {
            navigateTo(
                routeConstants.jobs.editAJob.root +
                    `${jobs.newJobData?.id}/` +
                    routeConstants.jobs.editAJob.advanceDetails
            );
        } else
            navigateTo(
                routeConstants.jobs.postAJob.root +
                    routeConstants.jobs.postAJob.advanceDetails
            );
    };

    const handleSubmit = async () => {
        const { hasError, errors } = getErrors(
            budgetDetails,
            budgetDetailsErrors as unknown as Record<string, string>
        );
        if (Number(budgetDetails.minAmount) > Number(budgetDetails.maxAmount)) {
            errors.maxAmount = "Max amount should b greator than min amount";
        }
        setBudgetDetailsErrors(errors as unknown as BudgetDetails);

        if (!hasError && jobs?.newJobData) {
            dispatch(
                updateNewJobData({
                    ...jobs.newJobData,
                    job_budget: {
                        payType: budgetDetails?.payType || "",
                        durationCycle: budgetDetails?.durationCycle || "",
                        currency: budgetDetails?.currency || "",
                        minAmount: budgetDetails?.minAmount || "",
                        maxAmount: budgetDetails?.maxAmount || "",
                        additionalCompensation:
                            budgetDetails?.additionalCompensation || "",
                        status: budgetDetails.status || "",
                    },
                })
            );
            const { job_category, job_details, job_location, job_purpose } =
                jobs?.newJobData;
            const data: CreateUpdateJobParams = {
                agency_id: user?.agency?.id ?? "",
                data: {
                    agency_category_id: job_category ?? -1,
                    job_purpose_id: job_purpose?.id ?? -1,
                    country_name: job_location?.country_name ?? "",
                    city: job_location?.city ?? "",
                    title: job_details?.jobInfo.title ?? "",
                    description: job_details?.jobInfo.description ?? "",
                    no_of_seats:
                        Number(job_details?.jobInfo.numberOfSeats) ?? -1,
                    gender: job_details?.dropdownInputs.gender ?? "",
                    duration:
                        job_details?.durationInputs.duration.duration ?? 1,
                    application_from:
                        job_location?.application_from === "worldwide"
                            ? "worldwide"
                            : null,
                    application_from_country_names:
                        job_location?.application_from_country_names,
                    duration_unit: "months",
                    applicant_min_age:
                        job_details?.durationInputs.minAge.duration ?? 1,
                    applicant_max_age:
                        job_details?.durationInputs.maxAge.duration ?? 1,
                    experience:
                        job_details?.durationInputs.experience.duration ?? 1,
                    experience_unit: "years",
                    start_date: job_details?.dateInputs?.start_date ?? "", // Add startDate
                    end_date: job_details?.dateInputs?.end_date ?? "", // Add endDate
                    status: budgetDetails.status ?? "",
                    qualification:
                        job_details?.dropdownInputs?.qualification ?? "",
                    language: job_details?.dropdownInputs?.language_id ?? "",
                    physical_details: {
                        min_weight: job_details?.textInputs?.minWeight ?? "",
                        max_weight: job_details?.textInputs?.maxWeight ?? "",
                        min_height: job_details?.textInputs?.minHeight ?? "",
                        max_height: job_details?.textInputs?.maxHeight ?? "",
                        hips: job_details?.textInputs?.hips ?? "",
                        waist: job_details?.textInputs?.waist ?? "",
                        bust: job_details?.textInputs?.bust ?? "",
                        eye_color: job_details?.dropdownInputs?.eye_color ?? "",
                        hair_color:
                            job_details?.dropdownInputs?.hair_color ?? "",
                        hair_length: job_details?.textInputs?.hair_length ?? "",
                        piercings: job_details?.dropdownInputs?.piercings ?? "",
                        ethnicity: job_details?.dropdownInputs?.ethnicity ?? "",
                        tattoos: job_details?.dropdownInputs?.tattoos ?? "",
                    },
                    budget: {
                        budget_type: budgetDetails.payType,
                        duration_cycle: budgetDetails.durationCycle,
                        min_amount: Number(budgetDetails.minAmount),
                        max_amount: Number(budgetDetails.maxAmount),
                        additional_compensation:
                            budgetDetails.additionalCompensation,
                        currency: budgetDetails.currency,
                    },
                },
            };

            try {
                if (isEdit) {
                    await updateJob({
                        ...data,
                        job_id: jobs.newJobData.id,
                    }).unwrap();
                } else {
                    await createJob(data).unwrap();
                }
            } catch (error: any) {
                if (error.status === 422) {
                    addToast({
                        type: "error",
                        text:
                            error.data.message ||
                            "You've reached the job casting limit for your subscription.",
                    });
                } else {
                    addToast({
                        type: "error",
                        text: "An unexpected error occurred. Please try again.",
                    });
                }
            }
        }
    };

    useEffect(() => {
        if (
            !jobs.newJobData?.job_purpose ||
            !jobs.newJobData.job_category ||
            !jobs.newJobData.job_location ||
            !jobs.newJobData.job_details
        ) {
            if (job_id) {
                navigateTo(
                    routeConstants.jobs.editAJob.root +
                        routeConstants.jobs.postAJob.category +
                        job_id
                );
            } else
                navigateTo(
                    routeConstants.jobs.postAJob.root +
                        routeConstants.jobs.postAJob.category
                );
        }
    }, [jobs, navigateTo, job_id]);
    useEffect(() => {
        if (jobs.newJobData?.job_budget) {
            const data = jobs.newJobData?.job_budget;

            setBudgetDetails({
                additionalCompensation: data?.additionalCompensation ?? "",
                currency: data?.currency ?? "",
                durationCycle: data?.durationCycle ?? "",
                maxAmount: data?.maxAmount ?? "",
                minAmount: data?.maxAmount ?? "",
                status: data?.status ? data.status : "",
                payType: data?.payType ?? "",
            });
        }
    }, [jobs.newJobData?.job_budget]);
    useEffect(() => {
        if (currencies && currencies.length > 0) {
            setCurrenciesData(
                currencies.map((currency) => ({
                    label: currency.iso_code,
                    value: currency.iso_code,
                }))
            );
        }
    }, [currencies]);

    useEffect(() => {
        if (isCreateSuccess || isUpdateSuccess) {
            if (isEdit) {
                const job_id = updateData.job.id;
                navigateTo(
                    routeConstants.jobs.editAJob.root +
                        `${jobs.newJobData?.id}/` +
                        routeConstants.jobs.postAJob.success,
                    {
                        state: {
                            job_id,
                        },
                    }
                );
            } else {
                const job_id = createData.job.id;
                navigateTo(
                    routeConstants.jobs.postAJob.root +
                        routeConstants.jobs.postAJob.success,
                    {
                        state: {
                            job_id,
                        },
                    }
                );
            }
        }
    }, [
        isEdit,
        isCreateSuccess,
        isUpdateSuccess,
        jobs.newJobData?.id,
        navigateTo,
        updateData,
        createData,
    ]);

    useEffect(() => {
        if (jobs.newJobData?.job_budget) {
            const data = jobs.newJobData?.job_budget;

            setBudgetDetails({
                ...data,
            });
        }
    }, [jobs.newJobData?.job_budget]);

    useEffect(() => {
        if (isCreateError) {
            addToast({
                type: "error",
                text:
                    (createError as ApiError)?.data?.message ||
                    "Something went wrong, please try again.",
            });
        }
    }, [isCreateError, createError]);

    return {
        agencyName: user.agency?.name ?? "agency",
        purposeName: jobs.newJobData?.job_purpose?.name ?? "purpose",
        location: `${jobs.newJobData?.job_location?.city}, ${jobs.newJobData?.job_location?.country_name}`,
        budgetDetails,
        budgetDetailsErrors,
        handleDropdownChange,
        handleTextInputChange,
        currenciesData,
        handleBack,
        handleSubmit,
        isFetching,
        isSubmitting: isCreateLoading || isUpdateLoading,
        startDate, // use startDate from useJobDetails
        endDate, // use endDate from useJobDetails
        handleJobDetailsStartDateChange,
        handleJobDetailsEndDateChange,
    };
};
